/**
 * Size / dimensional inventory math — pure functions, no Firestore.
 *
 * Used by:
 *   - Item master (ChartOfInventory) to validate defaults
 *   - Sale + Purchase line forms (SI/NSI/DC/SR/NSR/PI/NPI/IPI/PR/NPR) to
 *     compute SQft from L × H × Pieces
 *   - Print templates to render size-mode columns
 *
 * Convention: stockFlow `quantity` for size items is in SQft (the stock unit),
 * `unitPrice` is the rate per SQft, `lineTotal` = quantity * unitPrice. The
 * dimensions (length / height / thickness / pieces) live as descriptive
 * fields on the line and stockFlow row but never feed the accounting math
 * directly — they are inputs that produce the SQft quantity.
 */

// Divisor used to turn (L × H) into square feet for each supported unit.
//   inches: 1 sqft = 144 sq.in.
//   cm    : 1 sqft = 929.0304 cm² — using 929 for the conventional rounding
//           seen on slab/marble import bills.
const DIVISOR_BY_UNIT = {
  inches: 144,
  cm: 929,
};

const SUPPORTED_UNITS = Object.keys(DIVISOR_BY_UNIT);

/**
 * Normalise a unit string. Returns the canonical unit or 'inches' (default).
 */
export const normaliseSizeUnit = (unit) => {
  if (!unit) return 'inches';
  const lower = String(unit).toLowerCase();
  return SUPPORTED_UNITS.includes(lower) ? lower : 'inches';
};

/**
 * SQft area of ONE piece, given length × height in the item's chosen unit.
 * Thickness is descriptive only — it does NOT affect area math. Returns 0
 * when L or H is missing/non-positive so the UI can show "—" cleanly.
 */
export const computeSqftPerPiece = (length, height, unit) => {
  const L = Number(length) || 0;
  const H = Number(height) || 0;
  if (L <= 0 || H <= 0) return 0;
  const divisor = DIVISOR_BY_UNIT[normaliseSizeUnit(unit)];
  return (L * H) / divisor;
};

/**
 * Combined "Size" label for display: "L×H" or "L×H×T" when thickness is set.
 * Returns '' when L or H is missing, so callers can show a placeholder.
 */
export const buildSizeLabel = (length, height, thickness) => {
  const L = Number(length) || 0;
  const H = Number(height) || 0;
  if (L <= 0 || H <= 0) return '';
  const T = Number(thickness) || 0;
  return T > 0 ? `${L}×${H}×${T}` : `${L}×${H}`;
};

/**
 * Recompute the size-derived fields on a transaction line. Mutates the
 * returned shallow-copy (does not modify the input). The caller decides
 * whether to keep computed values or override (e.g. manual qty override).
 *
 *   Input line shape (size mode):
 *     { itemId, sizeUnit, length, height, thickness, pieces, unitPrice, ... }
 *   Output adds:
 *     { sqftPerPiece, totalSqft, sizeLabel, quantity, lineTotal }
 *
 * `quantity` is set to totalSqft so downstream accounting & stockFlow code
 * (which already reads `quantity` and `unitPrice`) continues to work without
 * branching on the size flag.
 */
export const recomputeSizeLine = (line) => {
  const unit = normaliseSizeUnit(line?.sizeUnit);
  const sqftPerPiece = computeSqftPerPiece(line?.length, line?.height, unit);
  const totalSqft = sqftPerPiece * (Number(line?.pieces) || 0);
  const rate = Number(line?.unitPrice) || 0;
  return {
    ...line,
    sizeUnit: unit,
    sqftPerPiece: round2(sqftPerPiece),
    totalSqft: round2(totalSqft),
    sizeLabel: buildSizeLabel(line?.length, line?.height, line?.thickness),
    // Stock + accounting use quantity. Keep it in sync with totalSqft so
    // every downstream calc (taxable amount, COGS, stock balance) works.
    quantity: round2(totalSqft),
    lineTotal: round2(totalSqft * rate),
  };
};

/**
 * Round to 2 decimal places to match the rest of the app's money math.
 */
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default {
  normaliseSizeUnit,
  computeSqftPerPiece,
  buildSizeLabel,
  recomputeSizeLine,
};
